import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';

import css from './WorkItemCard.module.css';

const WorkItemCard = ({
  className = null,
  rootClassName = null,
  title = null,
  description = null,
  imageUrl = null,
  url = null,
  metadata = null,
  viewMode = 'card', // 'card' or 'detailed'
  isPreview = false,
  onClick = null,
  intl = null,
}) => {

  // Extract metadata fields
  const siteName = metadata?.siteName;
  const favicon = metadata?.favicon;

  // Extract domain from URL
  const getDomain = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return null;
    }
  };

  const domain = url ? getDomain(url) : null;

  // Determine what to display
  const displayImage = imageUrl || metadata?.image;
  const sharetribeImageId = metadata?.imageId;
  const displayTitle = title || metadata?.title || (intl ? intl.formatMessage({ id: 'WorkItemCard.untitledWork' }) : 'Untitled Work');
  const displayDescription = description || metadata?.description;

  const classes = classNames(rootClassName || css.root, className);
  const cardClasses = classNames(css.card, {
    [css.cardDetailed]: viewMode === 'detailed',
    [css.cardPreview]: isPreview,
    [css.cardClickable]: !!onClick || !!url,
  });

  // Empty state for preview mode
  if (isPreview && !url && !title && !description && !displayImage) {
    return (
      <div className={classes}>
        <div className={cardClasses}>
          <div className={css.emptyState}>
            <svg className={css.emptyIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <p className={css.emptyText}><FormattedMessage id="WorkItemCard.cardPreviewEmpty" /></p>
          </div>
        </div>
      </div>
    );
  }

  // Card content
  const cardContent = (
    <>
      <div className={css.imageContainer}>
        {displayImage ? (
          // Use CSS background image for all images to prevent squishing
          <div
            className={css.image}
            style={{ backgroundImage: `url(${displayImage})` }}
            role="img"
            aria-label={displayTitle}
          />
        ) : (
          <div className={css.imagePlaceholder}>
            <svg className={css.imagePlaceholderIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}
        <div className={css.imageOverlay} />
      </div>

      <div className={css.content}>
        {(siteName || domain) && (
          <div className={css.source}>
            {favicon && (
              <img src={favicon} alt="" className={css.favicon} />
            )}
            <span className={css.sourceText}>{siteName || domain}</span>
          </div>
        )}

        <h4 className={css.title}>{displayTitle}</h4>

        {displayDescription && (
          <p className={css.description}>
            {viewMode === 'detailed'
              ? displayDescription
              : (displayDescription.length > 100
                ? `${displayDescription.substring(0, 100)}...`
                : displayDescription)
            }
          </p>
        )}

        {url && (
          <div className={css.footer}>
            <span className={css.link}>
              <FormattedMessage id="WorkItemCard.viewProject" />
              <svg className={css.arrowIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M7 17L17 7M17 7H7M17 7V17" />
              </svg>
            </span>
          </div>
        )}
      </div>
    </>
  );

  // Wrap in anchor tag if URL exists and not preview mode
  if (url && !isPreview && !onClick) {
    return (
      <div className={classes}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={cardClasses}
        >
          {cardContent}
        </a>
      </div>
    );
  }

  // Wrap in button if onClick handler exists
  if (onClick) {
    return (
      <div className={classes}>
        <button
          type="button"
          onClick={onClick}
          className={cardClasses}
        >
          {cardContent}
        </button>
      </div>
    );
  }

  // Otherwise just a div
  return (
    <div className={classes}>
      <div className={cardClasses}>
        {cardContent}
      </div>
    </div>
  );
};

WorkItemCard.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  url: PropTypes.string,
  metadata: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    imageId: PropTypes.string,
    siteName: PropTypes.string,
    favicon: PropTypes.string,
  }),
  viewMode: PropTypes.oneOf(['card', 'detailed']),
  isPreview: PropTypes.bool,
  onClick: PropTypes.func,
  intl: intlShape,
};

export default injectIntl(WorkItemCard);